
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { User, UserRole, AuthState } from '../types';
import { authService, AuthResponse } from '../services/auth.service';
import { useToast } from './ToastContext';

interface AuthContextType extends AuthState {
  login: (email: string, pass: string) => Promise<AuthResponse>;
  register: (payload: any) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  checkAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { showToast } = useToast();
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const checkAuth = useCallback(() => {
    const savedUser = localStorage.getItem('coinnexs_user');
    const token = localStorage.getItem('coinnexs_token');

    if (savedUser && token) {
      try {
        const parsedUser = JSON.parse(savedUser);
        // Normalize role on load for safety
        if (parsedUser.role) {
          parsedUser.role = parsedUser.role.toUpperCase() as UserRole;
        }
        setState({
          user: parsedUser,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch (e) {
        localStorage.removeItem('coinnexs_user');
        localStorage.removeItem('coinnexs_token');
        setState({ user: null, isAuthenticated: false, isLoading: false });
      }
    } else {
      setState({ user: null, isAuthenticated: false, isLoading: false });
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email: string, pass: string): Promise<AuthResponse> => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await authService.login({ email, password: pass });

      if (response.success && response.token && response.user) {
        // Normalize role to uppercase to match UserRole enum
        const normalizedUser = {
          ...response.user,
          role: response.user.role.toUpperCase() as UserRole
        };
        localStorage.setItem('coinnexs_token', response.token);
        localStorage.setItem('coinnexs_user', JSON.stringify(normalizedUser));
        setState({ user: normalizedUser, isAuthenticated: true, isLoading: false });
        showToast(response.message || 'Login successful', 'success');
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
        showToast(response.message || 'Login failed', response.message?.includes('locked') ? 'warning' : 'error');
      }
      return response;
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      showToast('An error occurred during login', 'error');
      return { success: false, message: 'An error occurred' };
    }
  };

  const register = async (payload: any): Promise<AuthResponse> => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await authService.register(payload);
      if (response.success) {
        showToast(response.message || 'Registration successful', 'success');
      } else {
        showToast(response.message || 'Registration failed', 'error');
      }
      setState(prev => ({ ...prev, isLoading: false }));
      return response;
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      showToast('An error occurred during registration', 'error');
      return { success: false, message: 'An error occurred' };
    }
  };

  const logout = async () => {
    try {
      const response = await authService.logout();
      if (response.success) {
        showToast(response.message || 'Logged out successfully', 'success');
      } else {
        showToast(response.message || 'Session cleared', 'warning');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('coinnexs_user');
      localStorage.removeItem('coinnexs_token');
      setState({ user: null, isAuthenticated: false, isLoading: false });
      window.location.hash = '/login';
    }
  };

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
